import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, Plus, Pencil, Trash2, Download, FileText, FileSpreadsheet,
  Eye, X, ChevronDown, ChevronUp, Users, Calculator, TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, Power
} from "lucide-react";

const fmt = (v) => `₹${(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/* Pick the SI tier whose sum_insured is closest to the member's SI */
function pickTier(tiers, sumInsured) {
  if (!tiers || tiers.length === 0) return null;
  const si = parseFloat(sumInsured);
  if (isNaN(si)) return tiers[0];
  return tiers.reduce((best, t) => Math.abs((t.sum_insured || 0) - si) < Math.abs((best.sum_insured || 0) - si) ? t : best, tiers[0]);
}

/* ─── Rate lookup — mirrors SubmitEndorsement.lookupRate, now graded-SI aware ─── */
function computeBase(rater, age, sumInsured) {
  const rt = rater?.rate_type || "age_band";
  let container = rater;
  let tierSI = null;
  if (rater?.graded_si && (rater.si_tiers || []).length > 0) {
    const tier = pickTier(rater.si_tiers, sumInsured);
    if (!tier) return { error: "No Sum Insured tiers configured on this card", ageRelevant: rt === "age_band" };
    container = tier;
    tierSI = tier.sum_insured;
  }
  if (rt === "flat_rate") {
    return container.flat_rate ? { rate: container.flat_rate, ageRelevant: false, tierSI } : { error: "No flat rate configured on this card", ageRelevant: false };
  }
  if (rt === "per_family") {
    return container.per_family_rate ? { rate: container.per_family_rate, ageRelevant: false, tierSI } : { error: "No per-family rate configured on this card", ageRelevant: false };
  }
  const bands = container?.age_bands || [];
  if (age === "" || age === null || age === undefined) return { needsAge: true, ageRelevant: true, tierSI, bands };
  const a = parseInt(age);
  if (isNaN(a) || a < 0) return { error: "Enter a valid age", ageRelevant: true, bands };
  const matchedIdx = bands.findIndex(ab => a >= ab.min_age && a <= ab.max_age);
  if (matchedIdx === -1) return { error: `Age ${a} is not covered by any band`, ageRelevant: true, matchedIdx: -1, tierSI, bands };
  return { rate: bands[matchedIdx].per_life_rate, ageRelevant: true, matchedIdx, tierSI, bands };
}

/* Detect gaps / overlaps in a set of age bands */
function analyzeBands(bands) {
  const issues = [];
  const sorted = [...bands].sort((a, b) => a.min_age - b.min_age);
  for (let i = 0; i < sorted.length; i++) {
    const b = sorted[i];
    if (b.min_age > b.max_age) issues.push({ type: "invalid", msg: `Band ${b.min_age}–${b.max_age} has min > max` });
    if (i > 0) {
      const prev = sorted[i - 1];
      if (b.min_age > prev.max_age + 1) issues.push({ type: "gap", msg: `Gap: ages ${prev.max_age + 1}–${b.min_age - 1} are uncovered` });
      if (b.min_age <= prev.max_age) issues.push({ type: "overlap", msg: `Overlap between ${prev.min_age}–${prev.max_age} and ${b.min_age}–${b.max_age}` });
    }
  }
  return issues;
}

/* ─── Sub-component: Single age-band row in editor ─── */
function BandRow({ band, index, onUpdate, onRemove }) {
  return (
    <div className="flex items-center gap-2 bg-stone-50 p-2 rounded-md" data-testid={`age-band-row-${index}`}>
      <span className="text-[10px] text-stone-400 w-5">{index + 1}.</span>
      <Input type="number" value={band.min_age} onChange={(e) => onUpdate(index, "min_age", e.target.value)} className="w-20 h-8 text-xs" placeholder="From" />
      <span className="text-stone-400 text-xs">–</span>
      <Input type="number" value={band.max_age} onChange={(e) => onUpdate(index, "max_age", e.target.value)} className="w-20 h-8 text-xs" placeholder="To" />
      <span className="text-stone-400 text-xs ml-2">₹</span>
      <Input type="number" value={band.per_life_rate} onChange={(e) => onUpdate(index, "per_life_rate", e.target.value)} className="w-32 h-8 text-xs" placeholder="Rate" />
      <Button type="button" size="icon" variant="ghost" onClick={() => onRemove(index)} className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0">
        <X className="w-3.5 h-3.5" />
      </Button>
    </div>
  );
}

/* ─── Sub-component: Age Band editor (list of BandRows) ─── */
function AgeBandEditor({ bands, onChange }) {
  const addBand = () => {
    const last = bands.length > 0 ? bands[bands.length - 1] : null;
    const nextMin = last ? last.max_age + 1 : 0;
    onChange([...bands, { min_age: nextMin, max_age: nextMin + 9, per_life_rate: 0 }]);
  };
  const removeBand = (idx) => onChange(bands.filter((_, i) => i !== idx));
  const updateBand = (idx, field, val) => {
    const updated = bands.slice();
    updated[idx] = { ...updated[idx], [field]: Number(val) || 0 };
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-stone-600">Age Bands & Per Life Rates</Label>
        <Button type="button" size="sm" variant="outline" onClick={addBand} className="text-xs gap-1 h-7" data-testid="add-age-band-btn">
          <Plus className="w-3 h-3" /> Add Band
        </Button>
      </div>
      {bands.length === 0 && <p className="text-xs text-stone-400 text-center py-3">No age bands. Click "Add Band".</p>}
      {bands.map((b, i) => <BandRow key={i} band={b} index={i} onUpdate={updateBand} onRemove={removeBand} />)}
    </div>
  );
}

/* ─── Sub-component: Graded Sum Insured tier editor ─── */
function SITierEditor({ rateType, tiers, onChange }) {
  const addTier = () => onChange([...tiers, { sum_insured: 0, flat_rate: 0, per_family_rate: 0, age_bands: [] }]);
  const removeTier = (idx) => onChange(tiers.filter((_, i) => i !== idx));
  const updateTier = (idx, field, val) => {
    const updated = tiers.slice();
    updated[idx] = { ...updated[idx], [field]: field === "age_bands" ? val : Number(val) || 0 };
    onChange(updated);
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-stone-600">Sum Insured Tiers</Label>
        <Button type="button" size="sm" variant="outline" onClick={addTier} className="text-xs gap-1 h-7" data-testid="add-si-tier-btn">
          <Plus className="w-3 h-3" /> Add SI Tier
        </Button>
      </div>
      {tiers.length === 0 && <p className="text-xs text-stone-400 text-center py-3">No tiers. Click "Add SI Tier".</p>}
      {tiers.map((t, i) => (
        <div key={i} className="border border-stone-200 rounded-lg p-3 space-y-2 bg-white" data-testid={`si-tier-${i}`}>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-stone-500 shrink-0">Sum Insured (₹)</Label>
            <Input type="number" value={t.sum_insured} onChange={(e) => updateTier(i, "sum_insured", e.target.value)} className="w-40 h-8 text-xs" placeholder="e.g. 500000" data-testid={`si-tier-amount-${i}`} />
            <div className="flex-1" />
            <Button type="button" size="icon" variant="ghost" onClick={() => removeTier(i)} className="h-7 w-7 text-red-400 hover:text-red-600 shrink-0"><X className="w-3.5 h-3.5" /></Button>
          </div>
          {rateType === "flat_rate" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-stone-500 shrink-0">Per Life Flat Rate (₹)</Label>
              <Input type="number" value={t.flat_rate ?? ""} onChange={(e) => updateTier(i, "flat_rate", e.target.value)} className="w-40 h-8 text-xs" placeholder="e.g. 5000" data-testid={`si-tier-flat-${i}`} />
            </div>
          )}
          {rateType === "per_family" && (
            <div className="flex items-center gap-2">
              <Label className="text-xs text-stone-500 shrink-0">Per Family Rate (₹)</Label>
              <Input type="number" value={t.per_family_rate ?? ""} onChange={(e) => updateTier(i, "per_family_rate", e.target.value)} className="w-40 h-8 text-xs" placeholder="e.g. 15000" data-testid={`si-tier-family-${i}`} />
            </div>
          )}
          {rateType === "age_band" && (
            <AgeBandEditor bands={t.age_bands || []} onChange={(b) => updateTier(i, "age_bands", b)} />
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── Sub-component: HR user picker chip ─── */
function HRChip({ user, selected, onToggle }) {
  return (
    <button
      type="button"
      onClick={() => onToggle(user.id)}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
        selected ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"
      }`}
      data-testid={`hr-assign-${user.username}`}
    >
      {user.full_name || user.username}{selected ? " ✓" : ""}
    </button>
  );
}

/* ─── Sub-component: Policy option in select ─── */
function PolicyOption({ policy }) {
  return <SelectItem value={policy.policy_number}>{policy.policy_number} — {policy.policy_type || "Policy"}</SelectItem>;
}

/* ─── Sub-component: Create / Edit dialog ─── */
function RaterFormDialog({ open, onClose, policies, hrUsers, editData, onSave }) {
  const [name, setName] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [rateType, setRateType] = useState("age_band");
  const [flatRate, setFlatRate] = useState("");
  const [perFamilyRate, setPerFamilyRate] = useState("");
  const [bands, setBands] = useState([]);
  const [gradedSi, setGradedSi] = useState(false);
  const [siTiers, setSiTiers] = useState([]);
  const [gstPercent, setGstPercent] = useState("18");
  const [isActive, setIsActive] = useState(true);
  const [selectedHR, setSelectedHR] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editData) {
      setName(editData.name || "");
      setPolicyNumber(editData.policy_number || "");
      setRateType(editData.rate_type || "age_band");
      setFlatRate(editData.flat_rate ?? "");
      setPerFamilyRate(editData.per_family_rate ?? "");
      setBands(editData.age_bands || []);
      setGradedSi(editData.graded_si || false);
      setSiTiers(editData.si_tiers || []);
      setGstPercent(editData.gst_percent != null ? String(editData.gst_percent) : "18");
      setIsActive(editData.is_active !== false);
      setSelectedHR(editData.assigned_hr_users || []);
    } else {
      setName(""); setPolicyNumber(""); setRateType("age_band"); setFlatRate(""); setPerFamilyRate(""); setBands([]);
      setGradedSi(false); setSiTiers([]); setGstPercent("18"); setIsActive(true); setSelectedHR([]);
    }
  }, [editData, open]);

  const toggleHR = (uid) => setSelectedHR((prev) => prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]);

  const handleSubmit = async () => {
    if (!name.trim() || !policyNumber) { toast.error("Name and Policy are required"); return; }
    if (gradedSi) {
      if (siTiers.length === 0) { toast.error("Add at least one Sum Insured tier"); return; }
      for (const t of siTiers) {
        if (!t.sum_insured || Number(t.sum_insured) <= 0) { toast.error("Each tier needs a Sum Insured amount"); return; }
        if (rateType === "flat_rate" && (!t.flat_rate || Number(t.flat_rate) <= 0)) { toast.error(`Tier ₹${t.sum_insured}: flat rate required`); return; }
        if (rateType === "per_family" && (!t.per_family_rate || Number(t.per_family_rate) <= 0)) { toast.error(`Tier ₹${t.sum_insured}: per-family rate required`); return; }
        if (rateType === "age_band" && (!t.age_bands || t.age_bands.length === 0)) { toast.error(`Tier ₹${t.sum_insured}: add at least one age band`); return; }
      }
    } else if (rateType === "age_band") {
      if (bands.length === 0) { toast.error("Add at least one age band"); return; }
      for (const b of bands) {
        if (b.min_age > b.max_age) { toast.error(`Invalid age range: ${b.min_age}-${b.max_age}`); return; }
        if (b.per_life_rate < 0) { toast.error("Rate cannot be negative"); return; }
      }
    } else if (rateType === "flat_rate") {
      if (!flatRate || Number(flatRate) <= 0) { toast.error("Flat rate is required"); return; }
    } else if (rateType === "per_family") {
      if (!perFamilyRate || Number(perFamilyRate) <= 0) { toast.error("Per family rate is required"); return; }
    }
    setSaving(true);
    try {
      const payload = {
        name, policy_number: policyNumber, rate_type: rateType,
        flat_rate: !gradedSi && rateType === "flat_rate" ? Number(flatRate) : null,
        per_family_rate: !gradedSi && rateType === "per_family" ? Number(perFamilyRate) : null,
        age_bands: !gradedSi && rateType === "age_band" ? bands : [],
        graded_si: gradedSi,
        si_tiers: gradedSi ? siTiers : [],
        gst_percent: gstPercent === "" ? 0 : Number(gstPercent),
        is_active: isActive,
        assigned_hr_users: selectedHR,
      };
      if (editData) {
        await axios.put(`${API}/raters/${editData.id}`, payload, { headers: hdrs() });
        toast.success("Rater updated");
      } else {
        await axios.post(`${API}/raters`, payload, { headers: hdrs() });
        toast.success("Rater created");
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rater");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="rater-form-dialog">
        <DialogHeader><DialogTitle className="text-lg">{editData ? "Edit Rater" : "Create New Rater"}</DialogTitle><DialogDescription>Define premium rate structure for a policy</DialogDescription></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-600">Rater Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. GMC Standard 2026-27" className="mt-1" data-testid="rater-name-input" />
            </div>
            <div>
              <Label className="text-xs text-stone-600">Policy</Label>
              <Select value={policyNumber} onValueChange={setPolicyNumber}>
                <SelectTrigger className="mt-1" data-testid="rater-policy-select"><SelectValue placeholder="Select Policy" /></SelectTrigger>
                <SelectContent>
                  {policies.map((p) => <PolicyOption key={p.policy_number} policy={p} />)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Rate Type Selector */}
          <div>
            <Label className="text-xs font-semibold text-stone-600">Rate Type</Label>
            <div className="mt-1.5 flex gap-2">
              {[
                { value: "age_band", label: "Age Band Rates" },
                { value: "flat_rate", label: "Per Life Flat Rate" },
                { value: "per_family", label: "Per Family Rate" },
              ].map(opt => (
                <button key={opt.value} type="button" onClick={() => setRateType(opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${rateType === opt.value ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-stone-50 border-stone-200 text-stone-500 hover:border-stone-300"}`}
                  data-testid={`rate-type-${opt.value}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Graded SI toggle + GST */}
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-start gap-2 cursor-pointer bg-stone-50 border border-stone-200 rounded-lg p-2.5" data-testid="graded-si-toggle">
              <input type="checkbox" checked={gradedSi} onChange={(e) => setGradedSi(e.target.checked)} className="mt-0.5 h-4 w-4 accent-indigo-600" />
              <span className="text-xs text-stone-600 leading-tight">
                <span className="font-semibold text-stone-700">Graded Sum Insured</span><br />
                Different rates per SI tier (member-wise)
              </span>
            </label>
            <div>
              <Label className="text-xs text-stone-600">GST % (additional)</Label>
              <Input type="number" value={gstPercent} onChange={(e) => setGstPercent(e.target.value)} placeholder="18" className="mt-1" data-testid="gst-percent-input" />
              <p className="text-[10px] text-amber-600 mt-1 font-medium">Rates are exclusive of GST — GST additional</p>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="rater-active-toggle">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
            <span className="text-xs text-stone-600"><span className="font-semibold text-stone-700">Active rate card for this policy</span> — used to price endorsements (only one can be active per policy)</span>
          </label>

          {/* Conditional Rate Input */}
          {!gradedSi && rateType === "flat_rate" && (
            <div>
              <Label className="text-xs text-stone-600">Per Life Flat Rate (₹)</Label>
              <Input type="number" value={flatRate} onChange={(e) => setFlatRate(e.target.value)} placeholder="e.g. 5000" className="mt-1 max-w-xs" data-testid="flat-rate-input" />
              <p className="text-[10px] text-stone-400 mt-1">Same rate for all members regardless of age</p>
            </div>
          )}
          {!gradedSi && rateType === "per_family" && (
            <div>
              <Label className="text-xs text-stone-600">Per Family Rate (₹)</Label>
              <Input type="number" value={perFamilyRate} onChange={(e) => setPerFamilyRate(e.target.value)} placeholder="e.g. 15000" className="mt-1 max-w-xs" data-testid="per-family-rate-input" />
              <p className="text-[10px] text-stone-400 mt-1">Single rate per family unit</p>
            </div>
          )}
          {!gradedSi && rateType === "age_band" && <AgeBandEditor bands={bands} onChange={setBands} />}
          {gradedSi && <SITierEditor rateType={rateType} tiers={siTiers} onChange={setSiTiers} />}
          <div>
            <Label className="text-xs font-semibold text-stone-600 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Assign to HR Users</Label>
            <div className="mt-1.5 flex flex-wrap gap-2">
              {hrUsers.length === 0 && <p className="text-xs text-stone-400">No HR users found</p>}
              {hrUsers.map((u) => <HRChip key={u.id} user={u} selected={selectedHR.includes(u.id)} onToggle={toggleHR} />)}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-rater-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {editData ? "Update" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Sub-component: View dialog (read-only) ─── */
function ViewBandRow({ band, index }) {
  return (
    <TableRow>
      <TableCell className="text-xs text-stone-400">{index + 1}</TableCell>
      <TableCell className="text-xs font-medium">{band.min_age} – {band.max_age} yrs</TableCell>
      <TableCell className="text-xs text-right font-semibold text-stone-800">{fmt(band.per_life_rate)}</TableCell>
    </TableRow>
  );
}

function RaterViewDialog({ open, onClose, rater }) {
  if (!rater) return null;
  const bands = rater.age_bands || [];
  const rt = rater.rate_type || "age_band";
  const graded = !!(rater.graded_si && (rater.si_tiers || []).length > 0);
  const gstPct = rater.gst_percent || 0;
  const tierLabel = rt === "flat_rate" ? "Per Life Flat" : rt === "per_family" ? "Per Family" : "Age Bands";
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rater-view-dialog">
        <DialogHeader><DialogTitle>{rater.name}</DialogTitle><DialogDescription>Rate card details</DialogDescription></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline">{rater.policy_number}</Badge>
            {rater.policy_type && <Badge variant="secondary">{rater.policy_type}</Badge>}
            {rater.insurer_name && <Badge variant="secondary">{rater.insurer_name}</Badge>}
            <Badge className={rt === "flat_rate" ? "bg-blue-100 text-blue-700" : rt === "per_family" ? "bg-purple-100 text-purple-700" : "bg-stone-100 text-stone-700"}>
              {rt === "flat_rate" ? "Per Life Flat Rate" : rt === "per_family" ? "Per Family Rate" : "Age Band Rates"}
            </Badge>
            {graded && <Badge className="bg-indigo-100 text-indigo-700">Graded SI</Badge>}
            {gstPct > 0 && <Badge className="bg-amber-100 text-amber-700">GST {gstPct}% additional</Badge>}
          </div>
          {gstPct > 0 && <p className="text-[11px] text-amber-600 font-medium">All rates are exclusive of GST — GST {gstPct}% additional, applied on the final endorsement amount.</p>}

          {graded ? (
            <div className="space-y-3">
              {(rater.si_tiers || []).map((t, ti) => (
                <div key={ti} className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/40" data-testid={`view-si-tier-${ti}`}>
                  <p className="text-xs font-semibold text-indigo-700 mb-2">Sum Insured: {fmt(t.sum_insured)}</p>
                  {rt === "flat_rate" && <p className="text-sm font-bold text-blue-800">{fmt(t.flat_rate)} <span className="text-[10px] font-normal text-stone-500">per life</span></p>}
                  {rt === "per_family" && <p className="text-sm font-bold text-purple-800">{fmt(t.per_family_rate)} <span className="text-[10px] font-normal text-stone-500">per family</span></p>}
                  {rt === "age_band" && (t.age_bands || []).length > 0 && (
                    <Table>
                      <TableHeader><TableRow className="bg-white/50"><TableHead className="text-[10px]">Age Range</TableHead><TableHead className="text-[10px] text-right">Per Life Rate</TableHead></TableRow></TableHeader>
                      <TableBody>{(t.age_bands || []).map((ab, i) => <TableRow key={i}><TableCell className="text-xs">{ab.min_age}–{ab.max_age} yrs</TableCell><TableCell className="text-xs text-right font-semibold">{fmt(ab.per_life_rate)}</TableCell></TableRow>)}</TableBody>
                    </Table>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
          {rt === "flat_rate" && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs text-blue-600 mb-1">Per Life Flat Rate</p>
              <p className="text-2xl font-bold text-blue-800">{fmt(rater.flat_rate)}</p>
            </div>
          )}
          {rt === "per_family" && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
              <p className="text-xs text-purple-600 mb-1">Per Family Rate</p>
              <p className="text-2xl font-bold text-purple-800">{fmt(rater.per_family_rate)}</p>
            </div>
          )}
          {rt === "age_band" && bands.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="text-xs font-semibold">#</TableHead>
                  <TableHead className="text-xs font-semibold">Age Range</TableHead>
                  <TableHead className="text-xs font-semibold text-right">Per Life Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bands.map((ab, i) => <ViewBandRow key={i} band={ab} index={i} />)}
              </TableBody>
            </Table>
          )}
            </>
          )}
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Sub-component: Expanded age-band table inside card ─── */
function ExpandedBandRow({ band, index }) {
  return (
    <TableRow className="hover:bg-stone-50/50">
      <TableCell className="text-xs text-stone-400 py-2">{index + 1}</TableCell>
      <TableCell className="text-xs font-medium py-2">{band.min_age} – {band.max_age} yrs</TableCell>
      <TableCell className="text-xs text-right font-semibold text-stone-800 py-2">{fmt(band.per_life_rate)}</TableCell>
    </TableRow>
  );
}

function HRNameBadge({ name }) {
  return <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">{name}</Badge>;
}

/* ─── Sub-component: Single rater card ─── */
function RaterCard({ rater, isAdmin, isExpanded, onToggle, onView, onEdit, onDelete, onDownload, onTest, onSetActive }) {
  const bands = rater.age_bands || [];
  const hrNames = rater.assigned_hr_names || [];
  const rt = rater.rate_type || "age_band";

  return (
    <Card className="hover:shadow-md transition-all" data-testid={`rater-card-${rater.id}`}>
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4 cursor-pointer" onClick={onToggle} data-testid={`rater-toggle-${rater.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-blue-50 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-indigo-600" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-stone-800 text-sm truncate">{rater.name}</h3>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <Badge variant="outline" className="text-[10px]">{rater.policy_number}</Badge>
                {rater.policy_type && <Badge variant="secondary" className="text-[10px]">{rater.policy_type}</Badge>}
                <Badge className={`text-[10px] ${rt === "flat_rate" ? "bg-blue-100 text-blue-700" : rt === "per_family" ? "bg-purple-100 text-purple-700" : "bg-stone-100 text-stone-700"}`}>
                  {rt === "flat_rate" ? "Flat Rate" : rt === "per_family" ? "Per Family" : (rater.graded_si ? "Age Bands" : `${bands.length} Age Bands`)}
                </Badge>
                {rater.graded_si && (rater.si_tiers || []).length > 0 && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">{rater.si_tiers.length} SI Tiers</Badge>}
                {(rater.gst_percent || 0) > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-700">+{rater.gst_percent}% GST</Badge>}
                {rater.is_active && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">● Active</Badge>}
                {!rater.graded_si && rt === "flat_rate" && <span className="text-[10px] text-blue-600 font-semibold">{fmt(rater.flat_rate)}</span>}
                {!rater.graded_si && rt === "per_family" && <span className="text-[10px] text-purple-600 font-semibold">{fmt(rater.per_family_rate)}</span>}
                {isAdmin && hrNames.length > 0 && (
                  <span className="text-[10px] text-emerald-600 flex items-center gap-0.5"><Users className="w-3 h-3" />{hrNames.length} HR</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onView(); }} data-testid={`view-rater-${rater.id}`}><Eye className="w-4 h-4 text-stone-500" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Test Premium" onClick={(e) => { e.stopPropagation(); onTest(); }} data-testid={`test-rater-${rater.id}`}><Calculator className="w-4 h-4 text-indigo-600" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download Excel" onClick={(e) => { e.stopPropagation(); onDownload("xlsx"); }} data-testid={`download-xlsx-${rater.id}`}><FileSpreadsheet className="w-4 h-4 text-emerald-600" /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download PDF" onClick={(e) => { e.stopPropagation(); onDownload("pdf"); }} data-testid={`download-pdf-${rater.id}`}><Download className="w-4 h-4 text-red-500" /></Button>
            {isAdmin && (
              <>
                {!rater.is_active && <Button variant="ghost" size="icon" className="h-8 w-8" title="Set as active card" onClick={(e) => { e.stopPropagation(); onSetActive(); }} data-testid={`set-active-rater-${rater.id}`}><Power className="w-4 h-4 text-emerald-600" /></Button>}
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`edit-rater-${rater.id}`}><Pencil className="w-4 h-4 text-blue-500" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`delete-rater-${rater.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
              </>
            )}
            {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </div>
        </div>
        {isExpanded && (
          <div className="border-t px-4 pb-4">
            {rt === "flat_rate" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 text-center">
                <p className="text-xs text-blue-600">Per Life Flat Rate</p>
                <p className="text-xl font-bold text-blue-800">{fmt(rater.flat_rate)}</p>
              </div>
            )}
            {rt === "per_family" && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3 text-center">
                <p className="text-xs text-purple-600">Per Family Rate</p>
                <p className="text-xl font-bold text-purple-800">{fmt(rater.per_family_rate)}</p>
              </div>
            )}
            {rt === "age_band" && bands.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50/50">
                    <TableHead className="text-[10px] font-semibold w-12">#</TableHead>
                    <TableHead className="text-[10px] font-semibold">Age Range</TableHead>
                    <TableHead className="text-[10px] font-semibold text-right">Per Life Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bands.map((ab, i) => <ExpandedBandRow key={i} band={ab} index={i} />)}
                </TableBody>
              </Table>
            )}
            {isAdmin && hrNames.length > 0 && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-[10px] font-semibold text-stone-500 uppercase tracking-wider mb-1">Assigned HR Users</p>
                <div className="flex flex-wrap gap-1.5">
                  {hrNames.map((n, i) => <HRNameBadge key={i} name={n} />)}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── Sub-component: Rate Card Tester dialog ─── */
function RaterTesterDialog({ open, onClose, raters, lockedRater }) {
  const [raterId, setRaterId] = useState("");
  const [age, setAge] = useState("");
  const [showProrata, setShowProrata] = useState(false);
  const [inception, setInception] = useState("");
  const [expiry, setExpiry] = useState("");
  const [effDate, setEffDate] = useState("");
  const [members, setMembers] = useState("1");
  const [testSI, setTestSI] = useState("");

  useEffect(() => {
    if (!open) return;
    const initial = lockedRater ? lockedRater.id : "";
    setRaterId(initial);
    setAge(""); setShowProrata(false); setInception(""); setExpiry(""); setEffDate(""); setMembers("1"); setTestSI("");
  }, [open, lockedRater]);

  const rater = lockedRater || raters.find(r => r.id === raterId) || null;
  const rt = rater?.rate_type || "age_band";
  const graded = !!(rater?.graded_si && (rater?.si_tiers || []).length > 0);
  const gstPct = rater?.gst_percent || 0;

  const base = useMemo(() => (rater ? computeBase(rater, age, testSI) : null), [rater, age, testSI]);
  const bands = useMemo(() => (base?.bands || rater?.age_bands || []), [base, rater]);
  const bandIssues = useMemo(() => (rt === "age_band" ? analyzeBands(bands) : []), [rt, bands]);

  // For flat/age-band the premium is per-life; multiply by members. Per-family is a single unit.
  const perLife = base && base.rate != null ? base.rate : 0;
  const memberCount = Math.max(parseInt(members) || 1, 1);
  const annualTotal = rt === "per_family" ? perLife : perLife * memberCount;
  const annualGst = Math.round(annualTotal * gstPct / 100);
  const annualWithGst = annualTotal + annualGst;

  const prorata = useMemo(() => {
    if (!base || base.rate == null || !inception || !expiry || !effDate) return null;
    try {
      const inc = new Date(inception), exp = new Date(expiry), eff = new Date(effDate);
      const totalDays = Math.ceil((exp - inc) / 86400000);
      const elapsed = Math.ceil((eff - inc) / 86400000);
      const remaining = Math.max(totalDays - elapsed, 0);
      if (totalDays <= 0) return { error: "Expiry must be after inception" };
      const amount = Math.round((annualTotal * remaining) / Math.max(totalDays, 1));
      const gst = Math.round(amount * gstPct / 100);
      return { remaining, totalDays, amount, gst, total: amount + gst };
    } catch { return null; }
  }, [base, inception, expiry, effDate, annualTotal, gstPct]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rater-tester-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5 text-indigo-600" /> Rate Card Tester</DialogTitle>
          <DialogDescription>Preview the premium a card returns before relying on it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {!lockedRater && (
            <div>
              <Label className="text-xs text-stone-600">Rate Card</Label>
              <Select value={raterId} onValueChange={setRaterId}>
                <SelectTrigger className="mt-1" data-testid="tester-rater-select"><SelectValue placeholder="Select a rate card" /></SelectTrigger>
                <SelectContent>
                  {raters.map(r => <SelectItem key={r.id} value={r.id}>{r.name} — {r.policy_number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {rater && (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="text-[10px]">{rater.policy_number}</Badge>
                <Badge className={`text-[10px] ${rt === "flat_rate" ? "bg-blue-100 text-blue-700" : rt === "per_family" ? "bg-purple-100 text-purple-700" : "bg-stone-100 text-stone-700"}`}>
                  {rt === "flat_rate" ? "Per Life Flat Rate" : rt === "per_family" ? "Per Family Rate" : "Age Band Rates"}
                </Badge>
                {graded && <Badge className="text-[10px] bg-indigo-100 text-indigo-700">Graded SI</Badge>}
                {gstPct > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-700">GST {gstPct}% additional</Badge>}
              </div>

              {/* Inputs */}
              <div className="grid grid-cols-2 gap-3">
                {graded && (
                  <div>
                    <Label className="text-xs text-stone-600">Sum Insured (₹)</Label>
                    <Input type="number" value={testSI} onChange={(e) => setTestSI(e.target.value)} placeholder="e.g. 500000" className="mt-1" data-testid="tester-si-input" />
                  </div>
                )}
                {base?.ageRelevant && (
                  <div>
                    <Label className="text-xs text-stone-600">Test Age</Label>
                    <Input type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g. 35" className="mt-1" data-testid="tester-age-input" />
                  </div>
                )}
                {rt !== "per_family" && (
                  <div>
                    <Label className="text-xs text-stone-600">No. of Members</Label>
                    <Input type="number" min="1" value={members} onChange={(e) => setMembers(e.target.value)} className="mt-1" data-testid="tester-members-input" />
                  </div>
                )}
              </div>

              {/* Base premium result */}
              {base?.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700" data-testid="tester-base-error">
                  <AlertTriangle className="w-4 h-4 shrink-0" /> {base.error}
                </div>
              ) : base?.needsAge ? (
                <p className="text-xs text-stone-400 text-center py-2">Enter an age to see the matching rate.</p>
              ) : base?.rate != null ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="tester-base-result">
                  {graded && base.tierSI != null && (
                    <p className="text-[11px] text-indigo-600 mb-2 font-medium">Matched SI tier: {fmt(base.tierSI)}</p>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-600">{rt === "per_family" ? "Per Family Premium" : "Per Life Rate"}</p>
                      <p className="text-2xl font-bold text-emerald-800">{fmt(base.rate)}</p>
                    </div>
                    {rt !== "per_family" && memberCount > 1 && (
                      <div className="text-right">
                        <p className="text-xs text-emerald-600">× {memberCount} members</p>
                        <p className="text-2xl font-bold text-emerald-800">{fmt(annualTotal)}</p>
                      </div>
                    )}
                  </div>
                  {gstPct > 0 && (
                    <div className="mt-3 pt-3 border-t border-emerald-200 text-xs space-y-1" data-testid="tester-gst-breakdown">
                      <div className="flex justify-between text-emerald-700"><span>Annual (excl. GST)</span><span>{fmt(annualTotal)}</span></div>
                      <div className="flex justify-between text-amber-700"><span>GST @ {gstPct}%</span><span>{fmt(annualGst)}</span></div>
                      <div className="flex justify-between font-bold text-emerald-900"><span>Total incl. GST</span><span>{fmt(annualWithGst)}</span></div>
                    </div>
                  )}
                </div>
              ) : null}

              {/* Age-band coverage table */}
              {rt === "age_band" && bands.length > 0 && (
                <div>
                  <Label className="text-xs font-semibold text-stone-600">Coverage</Label>
                  <Table className="mt-1">
                    <TableHeader>
                      <TableRow className="bg-stone-50">
                        <TableHead className="text-[10px] font-semibold">Age Range</TableHead>
                        <TableHead className="text-[10px] font-semibold text-right">Per Life Rate</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bands.map((ab, i) => (
                        <TableRow key={i} className={base?.matchedIdx === i ? "bg-emerald-50" : ""} data-testid={`tester-band-row-${i}`}>
                          <TableCell className="text-xs py-1.5 font-medium flex items-center gap-1">
                            {base?.matchedIdx === i && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />}
                            {ab.min_age} – {ab.max_age} yrs
                          </TableCell>
                          <TableCell className="text-xs py-1.5 text-right font-semibold">{fmt(ab.per_life_rate)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {bandIssues.length > 0 && (
                    <div className="mt-2 space-y-1" data-testid="tester-band-issues">
                      {bandIssues.map((iss, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          <AlertTriangle className="w-3 h-3 shrink-0" /> {iss.msg}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Prorata toggle */}
              {base?.rate != null && (
                <div className="border-t pt-3">
                  <button type="button" onClick={() => setShowProrata(v => !v)} className="flex items-center gap-1 text-xs font-semibold text-indigo-600" data-testid="tester-prorata-toggle">
                    {showProrata ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                    Prorata preview (optional)
                  </button>
                  {showProrata && (
                    <div className="mt-2 space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-stone-500">Inception</Label>
                          <Input type="date" value={inception} onChange={(e) => setInception(e.target.value)} className="mt-1 h-8 text-xs" data-testid="tester-inception-input" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-stone-500">Expiry</Label>
                          <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="mt-1 h-8 text-xs" data-testid="tester-expiry-input" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-stone-500">Effective (DOJ/DOL)</Label>
                          <Input type="date" value={effDate} onChange={(e) => setEffDate(e.target.value)} className="mt-1 h-8 text-xs" data-testid="tester-eff-input" />
                        </div>
                      </div>
                      {prorata?.error ? (
                        <div className="text-xs text-red-600 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {prorata.error}</div>
                      ) : prorata ? (
                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2" data-testid="tester-prorata-result">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-indigo-600">Remaining <strong>{prorata.remaining}</strong>/{prorata.totalDays} days</span>
                            <span className="text-lg font-bold text-indigo-800 flex items-center gap-1"><TrendingUp className="w-4 h-4" />{fmt(prorata.amount)}</span>
                          </div>
                          {gstPct > 0 && (
                            <div className="pt-2 border-t border-indigo-200 text-xs space-y-1">
                              <div className="flex justify-between text-indigo-700"><span>Prorata (excl. GST)</span><span>{fmt(prorata.amount)}</span></div>
                              <div className="flex justify-between text-amber-700"><span>GST @ {gstPct}%</span><span>{fmt(prorata.gst)}</span></div>
                              <div className="flex justify-between font-bold text-indigo-900"><span>Total to CD ledger</span><span>{fmt(prorata.total)}</span></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-[11px] text-stone-400">Fill all three dates to compute the prorated amount on {fmt(annualTotal)}.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Main Raters Page ─── */
export default function Raters({ isAdmin = false }) {
  const [raters, setRaters] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [hrUsers, setHrUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [testerOpen, setTesterOpen] = useState(false);
  const [lockedRater, setLockedRater] = useState(null);

  const fetchRaters = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/raters`, { headers: hdrs() });
      setRaters(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchRaters();
    if (isAdmin) {
      axios.get(`${API}/policies`, { headers: hdrs() }).then(r => setPolicies(r.data || [])).catch(() => {});
      axios.get(`${API}/users`, { headers: hdrs() }).then(r => setHrUsers((r.data || []).filter(u => u.role === "HR"))).catch(() => {});
    }
  }, [fetchRaters, isAdmin]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete rater "${name}"?`)) return;
    try {
      await axios.delete(`${API}/raters/${id}`, { headers: hdrs() });
      toast.success("Rater deleted");
      fetchRaters();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleSetActive = async (id) => {
    try {
      await axios.post(`${API}/raters/${id}/set-active`, {}, { headers: hdrs() });
      toast.success("Marked as active rate card");
      fetchRaters();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to set active"); }
  };

  const handleDownload = async (id, name, format) => {
    try {
      const res = await axios.get(`${API}/raters/${id}/download?format=${format}`, { headers: hdrs(), responseType: "blob" });
      const ext = format === "pdf" ? "pdf" : "xlsx";
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${name.replace(/\s+/g, "_")}_RateCard.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded as ${ext.toUpperCase()}`);
    } catch (err) { toast.error("Download failed"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#E05A47]" /></div>;

  return (
    <div className="space-y-6" data-testid="raters-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Rate Cards</h1>
          <p className="text-sm text-stone-500 mt-0.5">
            {isAdmin ? "Manage per-life premium rates by age range for each policy" : "View rate cards assigned to your policies"}
          </p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => { setLockedRater(null); setTesterOpen(true); }} className="gap-1.5" data-testid="open-tester-btn">
              <Calculator className="w-4 h-4" /> Test Premium
            </Button>
            <Button onClick={() => { setEditData(null); setFormOpen(true); }} className="gap-1.5" data-testid="create-rater-btn">
              <Plus className="w-4 h-4" /> New Rater
            </Button>
          </div>
        )}
      </div>

      {raters.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><p className="text-stone-400">No rate cards found</p></CardContent></Card>
      ) : (
        <div className="space-y-4">
          {raters.map((r) => (
            <RaterCard
              key={r.id}
              rater={r}
              isAdmin={isAdmin}
              isExpanded={expandedId === r.id}
              onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
              onView={() => { setViewData(r); setViewOpen(true); }}
              onEdit={() => { setEditData(r); setFormOpen(true); }}
              onDelete={() => handleDelete(r.id, r.name)}
              onDownload={(f) => handleDownload(r.id, r.name, f)}
              onTest={() => { setLockedRater(r); setTesterOpen(true); }}
              onSetActive={() => handleSetActive(r.id)}
            />
          ))}
        </div>
      )}

      {isAdmin && (
        <RaterFormDialog
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditData(null); }}
          policies={policies}
          hrUsers={hrUsers}
          editData={editData}
          onSave={fetchRaters}
        />
      )}
      <RaterViewDialog open={viewOpen} onClose={() => { setViewOpen(false); setViewData(null); }} rater={viewData} />
      {isAdmin && (
        <RaterTesterDialog
          open={testerOpen}
          onClose={() => { setTesterOpen(false); setLockedRater(null); }}
          raters={raters}
          lockedRater={lockedRater}
        />
      )}
    </div>
  );
}
